import { notify } from '@affine/component';
import {
  WorkspaceLocalState,
  WorkspaceService,
} from '@affine/core/modules/workspace';
import { I18n } from '@affine/i18n';
import type {
  DatabaseOfflineCopyPort,
  DatabaseOfflineCopyRecord,
} from '@blocksuite/affine/blocks/database';
import type { FrameworkProvider } from '@toeverything/infra';

const recordKey = (databaseId: string) => `database-offline-pin:${databaseId}`;

/** One port per workspace, so every editor of a database shares its copy. */
const ports = new WeakMap<object, DatabaseOfflineCopyPort>();

/**
 * "Keep available offline": the setting lives in this device's workspace
 * state; shards are pulled by the existing nbstore sync into local storage
 * only, never into editor memory. Editors outside a workspace offer none.
 */
export function createDatabaseOfflineCopyPort(
  framework: FrameworkProvider
): DatabaseOfflineCopyPort | undefined {
  const workspace = framework.getOptional(WorkspaceService)?.workspace;
  if (!workspace) return undefined;
  let port = ports.get(workspace);
  if (port) return port;
  const state = framework.get(WorkspaceLocalState);
  port = {
    read: databaseId =>
      state.get<DatabaseOfflineCopyRecord>(recordKey(databaseId)),
    write: (databaseId, record) => state.set(recordKey(databaseId), record),
    pull: (docId, signal) => workspace.engine.doc.pullRemote(docId, signal),
    notify: event => {
      if (event.kind === 'started')
        notify({
          title: I18n.t(
            'com.streamlain.dataview.database.offlineCopy.started',
            {
              size: (event.bytes / (1024 * 1024)).toFixed(1),
            }
          ),
        });
      else if (event.kind === 'completed')
        notify.success({
          title: I18n.t(
            'com.streamlain.dataview.database.offlineCopy.completed'
          ),
        });
      else
        notify.error({
          title: I18n.t('com.streamlain.dataview.database.offlineCopy.failed', {
            reason: event.reason,
          }),
        });
    },
  };
  ports.set(workspace, port);
  return port;
}
